import { action, makeObservable, observable } from 'mobx';

export class LoadableArray<T> {
  @observable
  private _payload?: T[] = null;
  @observable
  private state: 'initial' | 'loading' | 'partially-loaded' | 'fully-loaded' | 'error' = 'initial';

  private _promise?: Promise<any> = null;

  constructor() {
    makeObservable(this);
  }

  public static toLoadableArray<Incoming, Result>(
    promise: Promise<Incoming[]>,
    transform: (incoming: Incoming[]) => Result[] = (value: any[]) => value
  ): LoadableArray<Result> {
    const loadable = new LoadableArray<Result>();

    loadable.setLoading();
    loadable._promise = promise;

    promise
      .then((result) => {
        loadable.setFullyLoaded(transform(result));
      })
      .catch((e) => {
        loadable.setError();
        console.error('Failed to load data. Error:', e);
      });
    return loadable;
  }

  public get payload(): T[] {
    return this._payload;
  }

  public get promise(): Promise<any> {
    return this._promise;
  }

  public get isInitial(): boolean {
    return this.state === 'initial';
  }

  public get isLoading(): boolean {
    return this.state === 'loading';
  }

  public get isInitialLoading(): boolean {
    return this.isInitial || (this.isLoading && this.payload == null);
  }

  public get isPartiallyLoaded(): boolean {
    return this.state === 'partially-loaded';
  }

  public get isFullyLoaded(): boolean {
    return this.state === 'fully-loaded';
  }

  public get isLoaded(): boolean {
    return this.isPartiallyLoaded || this.isFullyLoaded;
  }

  public get hasFailed(): boolean {
    return this.state === 'error';
  }

  public get isLoadedAndEmpty(): boolean {
    return this.isLoaded && this.payload.length === 0;
  }

  public loadMore(promise: Promise<T[]>, isFullyLoaded: (result: T[]) => boolean): void {
    this.setLoading();

    promise
      .then((result: T[]) => {
        if (isFullyLoaded(result)) {
          this.setFullyLoaded(result);
        } else {
          this.setPartiallyLoaded(result);
        }
      })
      .catch((e) => {
        this.setError();
        console.error('Failed to load data. Error:', e);
      });
  }

  @action.bound
  private setFullyLoaded(payload: T[]): void {
    this.state = 'fully-loaded';
    this.addToPayload(payload);
  }

  @action.bound
  private setPartiallyLoaded(payload: T[]): void {
    this.state = 'partially-loaded';
    this.addToPayload(payload);
  }

  @action.bound
  private setLoading(): void {
    this.state = 'loading';
  }

  @action.bound
  private setError() {
    this.state = 'error';
    this._payload = null;
  }

  private addToPayload(payload: T[]) {
    if (this._payload == null) {
      this._payload = [];
    }
    payload.forEach((value: T) => {
      this._payload.push(value);
    });
  }
}
